/**
 * Force phone PWAs (Safari Add to Home) to pick up new deploys.
 * - Registers SW, checks for updates on load/focus
 * - Auto-reloads once when a new SW takes control
 * - Exposes forceHardRefresh() for Settings / admin-style "Update app"
 */

const VERSION_URL = '/version.json';
const SW_PATH = '/sw.js';
const RELOAD_FLAG = 'lp_sw_reloaded_v1';
const VERSION_KEY = 'lp_app_version_v1';

export async function forceHardRefresh() {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
    localStorage.removeItem(VERSION_KEY);
  } catch {
    /* ignore */
  }
  const url = new URL(window.location.href);
  url.searchParams.set('_r', String(Date.now()));
  window.location.replace(url.toString());
}

async function checkVersionFile() {
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return;
    const data = await res.json();
    const next = String(data.version || data.build || '');
    if (!next) return;
    const prev = localStorage.getItem(VERSION_KEY);
    if (prev && prev !== next) {
      localStorage.setItem(VERSION_KEY, next);
      await forceHardRefresh();
      return;
    }
    if (!prev) localStorage.setItem(VERSION_KEY, next);
  } catch {
    /* ignore offline */
  }
}

export function registerPwaUpdates({ onUpdateAvailable } = {}) {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return () => {};

  let cancelled = false;

  const onControllerChange = () => {
    if (cancelled) return;
    try {
      if (sessionStorage.getItem(RELOAD_FLAG) === '1') return;
      sessionStorage.setItem(RELOAD_FLAG, '1');
    } catch {
      /* ignore */
    }
    window.location.reload();
  };

  navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

  const setup = async () => {
    try {
      const reg = await navigator.serviceWorker.register(SW_PATH, { scope: '/' });
      const promptIfWaiting = () => {
        if (reg.waiting) {
          if (typeof onUpdateAvailable === 'function') {
            onUpdateAvailable(() => {
              reg.waiting.postMessage({ type: 'SKIP_WAITING' });
            });
          } else {
            reg.waiting.postMessage({ type: 'SKIP_WAITING' });
          }
        }
      };
      promptIfWaiting();
      reg.addEventListener('updatefound', () => {
        const worker = reg.installing;
        if (!worker) return;
        worker.addEventListener('statechange', () => {
          if (worker.state === 'installed' && navigator.serviceWorker.controller) {
            promptIfWaiting();
          }
        });
      });
      const tick = () => {
        reg.update().catch(() => undefined);
        void checkVersionFile();
      };
      tick();
      const onFocus = () => tick();
      window.addEventListener('focus', onFocus);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') tick();
      });
      const interval = window.setInterval(tick, 60_000);
      return () => {
        window.removeEventListener('focus', onFocus);
        window.clearInterval(interval);
      };
    } catch {
      return () => {};
    }
  };

  let innerCleanup = () => {};
  void setup().then((fn) => {
    if (typeof fn === 'function') innerCleanup = fn;
  });

  return () => {
    cancelled = true;
    navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    innerCleanup();
  };
}
